import { defineStore } from 'pinia'

// Services
import LicensesService from '@/services/licenses'
import LicenseReportService from '@/services/licenseReport'

const DEFAULT_ROWS_PER_PAGE = 25

// Mismo default que el backend, para que la primera carga no se vea ordenada de una forma y el
// servidor la haya resuelto de otra.
const DEFAULT_SORT_BY = 'id'

const defaultPagination = () => ({
  page: 1,
  rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  rowsNumber: 0,
  sortBy: DEFAULT_SORT_BY,
  descending: false
})

// La paginacion viene anidada en `meta`, no aplanada en la raiz de la respuesta. Leerla de `data`
// no lanza error: deja los tres campos en undefined y la tabla se queda sin paginador.
//
// El orden no viaja en `meta`: lo pone el cliente. Se reinyecta el que se acaba de pedir, porque si
// no la tabla perderia sus flechitas en cuanto respondiera el servidor.
const toPagination = (meta, { sortBy, descending }) => ({
  page: meta.current_page,
  rowsPerPage: Number(meta.per_page),
  rowsNumber: meta.total,
  sortBy,
  descending
})

const toSortDir = (descending) => (descending ? 'desc' : 'asc')

// `status` viene siempre y el contrato dice que siempre entero. Se coacciona igual porque la tabla
// decide con `Boolean(status)`, y un "0" en cadena se leeria como licencia activa.
const normalizeUser = (user) => ({
  ...user,
  status: Number(user.status)
})

// KPI del Reporte de Licencias, fase 2 (TKT-2285). Se normaliza a camelCase con default 0 en los
// ocho campos: el dashboard (LicenseKpiDashboard) hace aritmetica con ellos (porcentajes,
// division por demoQuota) y un `undefined` del backend ahi se traduce en NaN visible, no en un
// error que se note en desarrollo.
const defaultSummary = () => ({
  licensesTotal: 0,
  licensesActive: 0,
  licensesSuspended: 0,
  subutilizedCount: 0,
  subutilizedThresholdDays: 0,
  demoQuota: 0,
  demoUsed: 0,
  standardUsed: 0
})

// El endpoint responde { data: {...}, scope: {...} }: normalizeSummary recibe
// solo el objeto `data` interno, ya desenvuelto por quien la llama.
const normalizeSummary = (data) => ({
  licensesTotal: Number(data.licenses_total) || 0,
  licensesActive: Number(data.licenses_active) || 0,
  licensesSuspended: Number(data.licenses_suspended) || 0,
  subutilizedCount: Number(data.licenses_underutilized) || 0,
  subutilizedThresholdDays: Number(data.underutilized_threshold_days) || 0,
  demoQuota: Number(data.demo_quota) || 0,
  demoUsed: Number(data.demo_used) || 0,
  standardUsed: Number(data.standard_used) || 0
})

// KPI global de la plataforma, fase 3 (TKT-2285). Alcance distinto del `summary` de arriba: no es
// por organizacion/unidad de negocio, es la suma de TODAS. Sin `demo_quota` ni `standard_used` en
// el contrato del endpoint -- no se inventan aqui, LicenseGlobalKpiCounter solo pide los cuatro
// campos que el ticket pide mostrar (activas, demo, suspendidas, inutilizadas).
const defaultGlobalSummary = () => ({
  licensesTotal: 0,
  licensesActive: 0,
  licensesSuspended: 0,
  demoUsed: 0,
  subutilizedCount: 0,
  subutilizedThresholdDays: 0
})

const normalizeGlobalSummary = (data) => ({
  licensesTotal: Number(data.licenses_total) || 0,
  licensesActive: Number(data.licenses_active) || 0,
  licensesSuspended: Number(data.licenses_suspended) || 0,
  // El endpoint global usa `licenses_demo`, no `demo_used` como el de por-organizacion:
  // son dos contratos distintos, confirmado contra el backend real de esta misma fase.
  demoUsed: Number(data.licenses_demo) || 0,
  subutilizedCount: Number(data.licenses_underutilized) || 0,
  subutilizedThresholdDays: Number(data.underutilized_threshold_days) || 0
})

const useLicensesStore = defineStore('licenses', {
  state: () => ({
    organizations: [],
    organizationsPagination: defaultPagination(),
    franchises: [],
    franchisesPagination: defaultPagination(),
    users: [],
    usersPagination: defaultPagination(),
    currentOrganization: null,
    status: null,
    summary: defaultSummary(),
    summaryStatus: null,
    // KPI global de la plataforma, fase 3 (TKT-2285). Pieza separada de `summary`/`summaryStatus`
    // (que son por organizacion): comparten el mismo `status` los pisaria entre si igual que ya se
    // documenta arriba para `summary` vs `isLoading`.
    globalSummary: defaultGlobalSummary(),
    globalSummaryStatus: null,
    globalSummaryCachedAt: null,
    // Segundos, calculados por el backend (`cache_age_seconds`): la leyenda "actualizado hace X" se
    // arma sumando esto al reloj del cliente, nunca comparando `globalSummaryCachedAt` contra `new
    // Date()` local -- si el reloj del cliente esta desfasado del servidor, esa resta miente.
    globalSummaryCacheAgeSeconds: null
  }),
  getters: {
    isLoading: (state) => state.status === 'loading',
    // Estado propio, separado de `isLoading`: el KPI dashboard y la tabla de vista previa cargan en
    // paralelo (LicenseReportPage dispara ambos fetches al mismo cambio de alcance) y comparten
    // `status` los pisaria entre si -- el spinner de una tabla se apagaria antes de que su propio
    // fetch terminara.
    isSummaryLoading: (state) => state.summaryStatus === 'loading',
    isGlobalSummaryLoading: (state) => state.globalSummaryStatus === 'loading',
    franchiseById: (state) => (id) => state.franchises.find(franchise => franchise.id === id) || null,
    hasOrganization: (state) => (id) => Boolean(state.currentOrganization) && state.currentOrganization.id === id
  },
  actions: {
    // Nivel 1. Los conteos por organizacion vienen agregados del backend: no se derivan aqui.
    fetchOrganizations ({ page = 1, rowsPerPage = DEFAULT_ROWS_PER_PAGE, search = null, sortBy = DEFAULT_SORT_BY, descending = false } = {}) {
      this.status = 'loading'

      const promise = LicensesService.fetchOrganizations({
        page,
        limit: rowsPerPage,
        search,
        sortBy,
        sortDir: toSortDir(descending)
      })

      promise
        .then(({ data }) => {
          this.organizations = data.data
          this.organizationsPagination = toPagination(data.meta, { sortBy, descending })
          this.status = 'success'
        })
        .catch(() => {
          this.status = 'error'
        })

      return promise
    },

    // Nivel 2. La respuesta trae ademas el bloque `organization`, que resuelve el encabezado sin
    // depender de `GET /franchises/{id}` (que sigue teniendo fuga entre organizaciones).
    fetchFranchises ({ organizationId, page = 1, rowsPerPage = DEFAULT_ROWS_PER_PAGE, search = null, sortBy = DEFAULT_SORT_BY, descending = false }) {
      this.status = 'loading'

      const promise = LicensesService.fetchFranchises({
        organizationId,
        limit: rowsPerPage,
        page,
        search,
        sortBy,
        sortDir: toSortDir(descending)
      })

      promise
        .then(({ data }) => {
          this.franchises = data.data
          this.franchisesPagination = toPagination(data.meta, { sortBy, descending })
          this.currentOrganization = data.organization
          this.status = 'success'
        })
        .catch(() => {
          this.status = 'error'
        })

      return promise
    },

    // Nivel 3. Paginado, buscado y filtrado por estatus en servidor.
    //
    // `licenseType`/`assignedFrom`/`assignedTo` alimentan la vista previa del Reporte de Licencias
    // (TKT-2285, LicenseReportPage) -- ver el comentario en LicensesService.fetchUsers.
    fetchUsers ({
      organizationId,
      franchiseId = null,
      status = null,
      search = null,
      page = 1,
      rowsPerPage = DEFAULT_ROWS_PER_PAGE,
      sortBy = DEFAULT_SORT_BY,
      descending = false,
      licenseType = null,
      assignedFrom = null,
      assignedTo = null
    }) {
      this.status = 'loading'

      const promise = LicensesService.fetchUsers({
        organizationId,
        franchiseId,
        status,
        search,
        page,
        limit: rowsPerPage,
        sortBy,
        sortDir: toSortDir(descending),
        licenseType,
        assignedFrom,
        assignedTo
      })

      promise
        .then(({ data }) => {
          this.users = data.data.map(normalizeUser)
          this.usersPagination = toPagination(data.meta, { sortBy, descending })
          this.status = 'success'
        })
        .catch(() => {
          this.status = 'error'
        })

      return promise
    },

    // KPI del Reporte de Licencias, fase 2 (TKT-2285). Un solo objeto agregado por alcance, no
    // paginado -- por eso `summary` no sigue el patron `items` + `itemsPagination` del resto del
    // store. Se dispara al mismo cambio de organizacion/franchise que `fetchUsers` (ver el watch en
    // LicenseReportPage), no al cambio de `status`: el contrato documentado del endpoint solo acepta
    // organization_id y franchise_id.
    fetchSummary ({ organizationId, franchiseId = null }) {
      this.summaryStatus = 'loading'

      const promise = LicensesService.fetchSummary({ organizationId, franchiseId })

      promise
        .then(({ data }) => {
          // El endpoint responde { data: {...}, scope: {...} }: `data` aqui ya es el body completo
          // de axios, `data.data` es el objeto de conteos que normalizeSummary espera.
          this.summary = normalizeSummary(data.data)
          this.summaryStatus = 'success'
        })
        .catch(() => {
          this.summaryStatus = 'error'
        })

      return promise
    },

    // KPI global de la plataforma, fase 3 (TKT-2285). Sin alcance -- no recibe parametros. Se
    // dispara desde LicenseOrganizationsPage al montar y desde el boton "Sincronizar" (ver
    // syncGlobalSummary abajo), que reusa el mismo normalizador porque el backend responde el mismo
    // shape en ambos endpoints.
    fetchGlobalSummary () {
      this.globalSummaryStatus = 'loading'

      const promise = LicensesService.fetchGlobalSummary()

      promise
        .then(({ data }) => {
          this.globalSummary = normalizeGlobalSummary(data.data)
          this.globalSummaryCachedAt = data.cached_at
          this.globalSummaryCacheAgeSeconds = Number(data.cache_age_seconds) || 0
          this.globalSummaryStatus = 'success'
        })
        .catch(() => {
          this.globalSummaryStatus = 'error'
        })

      return promise
    },

    // Fuerza el recalculo del KPI global en vez de servir el valor cacheado (boton "Sincronizar").
    // Actualiza el mismo `globalSummary`/`globalSummaryCachedAt` que fetchGlobalSummary: para quien
    // consume el store no importa si el valor actual vino del cache o de un sync fresco.
    syncGlobalSummary () {
      this.globalSummaryStatus = 'loading'

      const promise = LicensesService.syncGlobalSummary()

      promise
        .then(({ data }) => {
          this.globalSummary = normalizeGlobalSummary(data.data)
          this.globalSummaryCachedAt = data.cached_at
          this.globalSummaryCacheAgeSeconds = Number(data.cache_age_seconds) || 0
          this.globalSummaryStatus = 'success'
        })
        .catch(() => {
          this.globalSummaryStatus = 'error'
        })

      return promise
    },

    /**
     * Escrituras. Resuelven con `affected`, el numero de cambios reales. Un `affected` en 0 no es un
     * error: significa que el alcance ya estaba en ese estado. Quien llama decide como decirlo.
     *
     * `reasonCode` es obligatorio para el backend; viaja tal cual lo eligio el usuario en el
     * dialogo de razon. El store no lo valida ni le pone default: fabricar una razon que nadie
     * eligio contamina justo la tabla de auditoria que esta feature existe para llenar.
     */
    setUsersStatus ({ ids, status, reasonCode, reasonDetail }) {
      return LicensesService.setUsersStatus({ userIds: ids, status, reasonCode, reasonDetail })
        .then(({ data }) => data.affected)
    },
    setFranchisesStatus ({ ids, status, reasonCode, reasonDetail }) {
      return LicensesService.setFranchisesStatus({ franchiseIds: ids, status, reasonCode, reasonDetail })
        .then(({ data }) => data.affected)
    },
    setOrganizationsStatus ({ ids, status, reasonCode, reasonDetail }) {
      return LicensesService.setOrganizationsStatus({ organizationIds: ids, status, reasonCode, reasonDetail })
        .then(({ data }) => data.affected)
    },

    // Reporte de Licencias (TKT-2285). Dispara el job async del backend y regresa de inmediato --
    // mismo patron "fire-and-forget + notificacion" que ReportsService.requestGeneralReportByNotification
    // para el Reporte General (ver NewReportsHeader.vue handleLocalDownload). Quien llama no espera
    // el archivo aqui: el link llega despues por el panel de notificaciones a /pdf-descarga/:token.
    requestReportExport (filters) {
      return LicenseReportService.requestByNotification(filters)
    },

    // PDF, fase 3 (TKT-2285). Mismo patron fire-and-forget que requestReportExport, otro endpoint.
    requestReportPdfExport (filters) {
      return LicenseReportService.requestPdfByNotification(filters)
    }
  }
})

export default useLicensesStore
